export class HotkeyManager {
    constructor(config) {
        this.appClient = config.appClient;
        this._bindings = new Map(); // name -> binding config
        this._handlers = new Map(); // name -> callback function

        this._boundKeyDown = this._onKeyDown.bind(this);
        // Attach immediately — no auth lifecycle needed for keyboard listeners
        document.addEventListener('keydown', this._boundKeyDown);
    }

    // No-op initialize so ApplicationClient can still call it uniformly
    initialize() {
        return Promise.resolve();
    }

    /**
     * Register a new configurable hotkey
     * @param {string} name Unique identifier for the action
     * @param {Object} defaultShortcut Default keys (e.g. { key: 'n', ctrlKey: true, altKey: true })
     * @param {string} description Human-readable description
     * @param {Function} callback Callback to execute when triggered
     */
    register(name, defaultShortcut, description, callback) {
        // Retrieve custom binding from shared data or local storage if any
        let custom = null;
        try {
            const stored = localStorage.getItem(`ab_hotkey_${name}`);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && typeof parsed.key === 'string') custom = parsed;
            }
        } catch (e) {
            console.warn("Failed to load custom keybinding for", name, e);
        }

        const shortcut = custom ? custom : defaultShortcut;

        if (this._bindings.has(name)) {
            console.warn(`Replacing already registered hotkey '${name}'.`);
        }
        this._bindings.set(name, {
            name,
            key: shortcut.key,
            ctrlKey: !!shortcut.ctrlKey,
            altKey: !!shortcut.altKey,
            shiftKey: !!shortcut.shiftKey,
            metaKey: !!shortcut.metaKey,
            description,
            repeat: !!shortcut.repeat,
            allowInEditable: shortcut.allowInEditable ?? Boolean(shortcut.ctrlKey || shortcut.altKey || shortcut.metaKey),
            defaultShortcut
        });

        if (callback) {
            this._handlers.set(name, callback);
        }
        return () => this.unregister(name);
    }

    /**
     * Unregister a hotkey
     */
    unregister(name) {
        this._bindings.delete(name);
        this._handlers.delete(name);
    }

    /**
     * Update/override a keybinding configuration
     */
    updateBinding(name, shortcut) {
        const binding = this._bindings.get(name);
        if (!binding) return;

        binding.key = shortcut.key;
        binding.ctrlKey = !!shortcut.ctrlKey;
        binding.altKey = !!shortcut.altKey;
        binding.shiftKey = !!shortcut.shiftKey;
        binding.metaKey = !!shortcut.metaKey;
        binding.repeat = !!shortcut.repeat;
        binding.allowInEditable = shortcut.allowInEditable ?? Boolean(shortcut.ctrlKey || shortcut.altKey || shortcut.metaKey);

        // Persist local custom configuration
        try {
            localStorage.setItem(`ab_hotkey_${name}`, JSON.stringify(shortcut));
        } catch (e) {
            console.error("Failed to save custom keybinding for", name, e);
        }
    }

    /**
     * Get all registered keybindings (useful for rendering options or settings UI)
     */
    getBindings() {
        return Array.from(this._bindings.values());
    }

    _onKeyDown(e) {
        const target = e.target;
        const isEditable = target instanceof Element && Boolean(target.closest("input, textarea, select, [contenteditable='true']"));

        for (const [name, binding] of this._bindings.entries()) {
            if ((e.repeat && !binding.repeat) || (isEditable && !binding.allowInEditable)) {
                continue;
            }

            const keyMatch = String(e.key).toLowerCase() === String(binding.key).toLowerCase();
            // Ctrl bindings act as the cross-platform primary modifier. Explicit Meta
            // bindings remain available and are no longer silently ignored.
            const ctrlMatch = binding.ctrlKey ? (e.ctrlKey || e.metaKey) : !e.ctrlKey;
            const altMatch = binding.altKey === e.altKey;
            const shiftMatch = binding.shiftKey === e.shiftKey;
            const metaMatch = binding.metaKey ? e.metaKey : (binding.ctrlKey ? true : !e.metaKey);

            if (keyMatch && ctrlMatch && altMatch && shiftMatch && metaMatch) {
                e.preventDefault();
                const handler = this._handlers.get(name);
                if (handler) {
                    handler(e);
                }
                break;
            }
        }
    }

    destroy() {
        document.removeEventListener('keydown', this._boundKeyDown);
        this._bindings.clear();
        this._handlers.clear();
    }
}
